import _ from "lodash";
import { api } from "../utils/api";
import UserStore from "../utils/UserStore";
import { applyCampusI18nOverrides } from "../utils/i18n";

const isAuthenticated = () => {
  return !!UserStore.getToken();
};

const authenticate = (email, password) => {
  const params = { email, password };
  return api
    .post("WaybeeUsers/Authenticate", params)
    .then(response => response.data.response);
};

const logout = () => {
  const token = UserStore.getToken();
  if (!token) {
    return true;
  }
  const params = { access_token: token };
  return api
    .post("WaybeeUsers/logout", { params })
    .finally(() => UserStore.clear());
};

const getSchoolUser = async userId => {
  const res = await api.get(`WaybeeUsers/${userId}/schoolUser`);
  return res.data;
};

const getLoginInfo = async userExternalId => {
  const res = await api.get("ClassDiaryV1/user/getLoginInfo", {
    params: { id: userExternalId }
  });
  return res.data;
};

const login = async (email, password) => {
  let userId = 0;
  try {
    const tokenData = await authenticate(email, password);
    UserStore.setToken(tokenData.id);
    userId = tokenData.userId;
  } catch (err) {
    if (!err.response) {
      throw new Error(`Falha ao conectar no servidor: ${err.message}` || "");
    }
    const { error } = err.response.data;

    throw new Error(error.message || "Erro ao realizar o login");
  }

  try {
    const user = await getSchoolUser(userId);
    UserStore.setUserName(user.name);
    UserStore.setAvailableCampus(user.userCampuses);
    const userCampus = user.userCampuses[0];
    if (userCampus) {
      UserStore.setCurrentCampus(userCampus.Campus.id);
      applyCampusI18nOverrides(userCampus.Campus.id);
      UserStore.setUserExternalId(userCampus.userExternalId);
      const campusName =
        userCampus.Campus &&
        (userCampus.Campus.name ||
          userCampus.Campus.nome ||
          userCampus.Campus.NOME);
      if (campusName) {
        UserStore.setSchoolName(campusName);
      }
      if (userCampus.userExternalId) {
        const externalUserInfo = await getLoginInfo(userCampus.userExternalId);
        UserStore.setUserExternalInfo(externalUserInfo.externalData);
        UserStore.setUserPhoto(externalUserInfo.photo);
      } else {
        UserStore.setUserExternalInfo({
          codProf: null,
          codPessoa: null,
          isCoordinator: false,
          isTeacher: false,
          username: null,
          userCampus: null
        });
        UserStore.setUserPhoto(user.photoProfile || "");
      }
    }
  } catch (err) {
    logout();
    const message = _.get(
      err,
      "response.data.error.message",
      "Usuário não cadastrado no sistema"
    );
    throw new Error(message);
  }
};

const getUserCampus = async () => {
  const res = UserStore.getAvailableCampus();
  if (!Array.isArray(res)) return [];
  return res
    .filter(userCampus => userCampus && userCampus.Campus)
    .map(userCampus => ({
      id: userCampus.Campus.id,
      name: userCampus.Campus.name
    }));
};

const getPendingAckCommunications = () => {
  return api.get("CommunicationInteractions/getPendingAckCommunications");
};

const updateAckCommunication = (ack, communicationId, userId) => {
  return api.post("CommunicationInteractions/updateAck", {
    ack,
    communicationId,
    userId
  });
};

const getMySessionInfos = async () => {
  return api.get("WaybeeUsers/mySessionInfo");
};

const apiErrorMessage = (err, fallback) => {
  if (!err || !err.response) {
    return (err && err.message) || fallback;
  }
  return (
    (err.response.data &&
      err.response.data.error &&
      err.response.data.error.message) ||
    fallback
  );
};

const forgotPassword = async identifier => {
  try {
    const res = await api.post("ClassDiaryV1/auth/forgot-password", {
      identifier
    });
    return res.data;
  } catch (err) {
    throw new Error(
      apiErrorMessage(err, "Não foi possível solicitar a recuperação de senha")
    );
  }
};

const resetPassword = async ({
  identifier,
  token,
  newPassword,
  confirmationPassword
}) => {
  try {
    const res = await api.post("ClassDiaryV1/auth/reset-password", {
      identifier,
      token,
      newPassword,
      confirmationPassword
    });
    return res.data;
  } catch (err) {
    throw new Error(apiErrorMessage(err, "Não foi possível redefinir a senha"));
  }
};

const changePassword = async ({
  lastPassword,
  newPassword,
  confirmationPassword
}) => {
  try {
    const res = await api.post("ClassDiaryV1/auth/change-password", {
      lastPassword,
      newPassword,
      confirmationPassword
    });
    return res.data;
  } catch (err) {
    throw new Error(apiErrorMessage(err, "Não foi possível alterar a senha"));
  }
};

const UserService = {
  isAuthenticated,
  authenticate,
  login,
  logout,
  getUserCampus,
  getMySessionInfos,
  getPendingAckCommunications,
  updateAckCommunication,
  forgotPassword,
  resetPassword,
  changePassword
};

export default UserService;
